import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Eye, Edit, Send, AlertCircle, Calendar, Users, CheckCircle, ClipboardList, Target, AlertTriangle, User, RefreshCw, ArrowRight, BarChart3, CalendarCheck, Lock, ShieldCheck } from 'lucide-react';
import api, { getEmployees, getAttendanceToday, bulkMarkAttendance, getAttendanceConfirmationStatus, getExplanations, submitExplanation } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import HRDashboard from './HRDashboard';
import './DepartmentDashboard.css';

function DepartmentDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['reports', 'common', 'plans', 'documents']);
  const isRu = i18n.language === 'ru';
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePlans, setActivePlans] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [resultText, setResultText] = useState('');
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [carriedTasks, setCarriedTasks] = useState([]);
  const [carryingOver, setCarryingOver] = useState(false);

  // Employee/attendance state
  const [deptEmployees, setDeptEmployees] = useState([]);
  const [deptAttendance, setDeptAttendance] = useState({});
  const [showAttendanceForm, setShowAttendanceForm] = useState(false);
  const [attSaving, setAttSaving] = useState(false);
  const [attConfirmedByHR, setAttConfirmedByHR] = useState(false); // заблокировано ли редактирование посещаемости
  const [myExplanations, setMyExplanations] = useState([]); // запрошенные объяснительные у текущего сотрудника
  const [expFile, setExpFile] = useState({}); // { expId: File }
  const [expUploading, setExpUploading] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    period: '',
    summary: '',
    main_work: '',
    achievements: '',
    problems: '',
    plans_next_period: ''
  });

  // Template state
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [departmentFields, setDepartmentFields] = useState({});
  const [lastReportData, setLastReportData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const userResponse = await api.getCurrentUser();
      setUser(userResponse.data);
      
      // Get periods
      const periodsResponse = await api.getPeriods();
      setPeriods(periodsResponse.data.results || periodsResponse.data);
      
      // Get user's reports, active plans and pending tasks
      if (userResponse.data.department_id) {
        const [reportsResponse, plansResponse, tasksResponse] = await Promise.all([
          api.getReports({ department: userResponse.data.department_id }),
          api.getMyPlans({ status: 'active' }),
          api.getMyPendingExecutions().catch(() => ({ data: [] })),
        ]);
        setReports(reportsResponse.data.results || reportsResponse.data);
        setActivePlans(plansResponse.data.results || plansResponse.data || []);
        setPendingTasks(tasksResponse.data || []);

        // Загрузка шаблонов отчётов отдела
        try {
          const templatesResp = await api.getMyReportTemplates();
          const tplData = templatesResp.data.results || templatesResp.data || [];
          setTemplates(tplData);
        } catch (e) {
          console.error('Error fetching report templates:', e);
        }

        // Загрузка еженедельного плана текущей недели
        try {
          const allPlans = plansResponse.data.results || plansResponse.data || [];
          const now = new Date();
          const weeklyPlans = allPlans.filter(p =>
            p.plan_type === 'weekly' &&
            new Date(p.start_date) <= now &&
            new Date(p.end_date) >= now
          );
          if (weeklyPlans.length > 0) {
            const currentWeeklyPlan = weeklyPlans[0];
            setWeeklyPlan(currentWeeklyPlan);
            // Загрузить задачи плана
            const tasksResp = await api.getPlanTasks({ plan: currentWeeklyPlan.id });
            const allTasks = tasksResp.data.results || tasksResp.data || [];
            setWeeklyTasks(allTasks);
            // Отделить перенесённые задачи
            setCarriedTasks(allTasks.filter(t => t.is_carried_over));
          } else {
            // Нет текущего еженедельного плана — проверим прошлый для переносов
            const pastWeekly = allPlans.filter(p => p.plan_type === 'weekly');
            if (pastWeekly.length > 0) {
              // Самый свежий plan
              const sorted = [...pastWeekly].sort((a, b) =>
                new Date(b.end_date) - new Date(a.end_date)
              );
              const lastPlan = sorted[0];
              if (new Date(lastPlan.end_date) < now) {
                // Прошедший план — покажем невыполненные задачи для переноса
                const tasksResp = await api.getPlanTasks({ plan: lastPlan.id });
                const allTasks = tasksResp.data.results || tasksResp.data || [];
                const unfinished = allTasks.filter(t =>
                  t.status !== 'completed' && t.status !== 'cancelled'
                );
                if (unfinished.length > 0) {
                  setCarriedTasks(unfinished);
                  setWeeklyPlan({ ...lastPlan, _isPast: true });
                }
              }
            }
          }
        } catch (e) {
          console.error('Error fetching weekly plan:', e);
        }

        // Загрузка сотрудников и посещаемости отдела
        try {
          const empRes = await getEmployees({ department: userResponse.data.department_id, is_active: true });
          const emps = empRes.data.results || empRes.data || [];
          setDeptEmployees(emps);
          // Сегодняшняя посещаемость
          const attRes = await getAttendanceToday({ department: userResponse.data.department_id });
          const records = attRes.data.results || attRes.data || [];
          const map = {};
          records.forEach(r => { map[r.employee] = { status: r.status, note: r.note || '' }; });
          emps.forEach(emp => { if (!map[emp.id]) map[emp.id] = { status: 'present', note: '' }; });
          setDeptAttendance(map);
          // Проверяем, подтвердил ли HR посещаемость этого отдела на сегодня
          try {
            const today = new Date().toISOString().split('T')[0];
            const confRes = await getAttendanceConfirmationStatus({ date: today });
            const confirmed = (confRes.data.confirmed_departments || []).some(
              c => String(c.department_id) === String(userResponse.data.department_id)
            );
            setAttConfirmedByHR(confirmed);
          } catch (_) {}
        } catch (e) {
          console.error('Error fetching employees:', e);
        }

        // Загрузка объяснительных текущего сотрудника (только requested/submitted)
        try {
          const expRes = await getExplanations({ employee: userResponse.data.id });
          const pending = (expRes.data.results || expRes.data || []).filter(
            e => e.status === 'requested' || e.status === 'submitted'
          );
          setMyExplanations(pending);
        } catch (_) {}
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const resetModal = () => {
    setShowCreateModal(false);
    setEditingReport(null);
    setFormData({
      title: '',
      period: '',
      summary: '',
      main_work: '',
      achievements: '',
      problems: '',
      plans_next_period: ''
    });
    setActiveTemplate(null);
    setDepartmentFields({});
    setLastReportData(null);
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setFormData({
      title: report.title || '',
      period: report.period ? String(report.period) : '',
      summary: report.summary || '',
      main_work: report.main_work || '',
      achievements: report.achievements || '',
      problems: report.problems || '',
      plans_next_period: report.plans_next_period || ''
    });

    // Загрузить department_data при редактировании
    if (report.department_data && report.department_data.data) {
      setDepartmentFields(report.department_data.data);
    }

    // Найти подходящий шаблон
    const selectedPeriod = periods.find(p => p.id === report.period);
    if (selectedPeriod) {
      const tpl = templates.find(t => t.period_type === selectedPeriod.type);
      setActiveTemplate(tpl || null);
    }

    setShowCreateModal(true);
  };

  const handlePeriodChange = async (periodId) => {
    setFormData(prev => ({ ...prev, period: periodId }));

    if (!periodId) {
      setActiveTemplate(null);
      setDepartmentFields({});
      setLastReportData(null);
      return;
    }

    // Найти выбранный период для определения типа
    const selectedPeriod = periods.find(p => String(p.id) === String(periodId));
    if (!selectedPeriod) return;

    // Найти шаблон для данного типа периода
    const tpl = templates.find(t => t.period_type === selectedPeriod.type);
    setActiveTemplate(tpl || null);

    // Инициализировать поля из шаблона
    if (tpl && tpl.fields_structure) {
      const initialFields = {};
      tpl.fields_structure.forEach(field => {
        initialFields[field.key] = '';
      });
      setDepartmentFields(initialFields);
    } else {
      setDepartmentFields({});
    }

    // Загрузить последний отчёт для авто-подстановки
    if (user?.department_id) {
      try {
        const lastResp = await api.getLastReport({
          department_id: user.department_id,
          period_type: selectedPeriod.type
        });
        setLastReportData(lastResp.data);
      } catch (e) {
        setLastReportData(null);
      }
    }
  };

  const handleSaveReport = async () => {
    try {
      if (!formData.title || !formData.period) {
        alert(t('reports:form.fillTitleAndPeriod'));
        return;
      }

      const reportData = {
        title: formData.title,
        period: parseInt(formData.period),
        department: user.department_id,
        summary: formData.summary || '',
        main_work: formData.main_work || '',
        achievements: formData.achievements || '',
        problems: formData.problems || '',
        plans_next_period: formData.plans_next_period || '',
      };

      // Добавить данные шаблона отдела
      if (activeTemplate && Object.keys(departmentFields).length > 0) {
        reportData.department_data = departmentFields;
      }

      if (editingReport) {
        await api.updateReport(editingReport.id, reportData);
        alert(t('reports:messages.updated'));
      } else {
        reportData.status = 'draft';
        await api.createReport(reportData);
        alert(t('reports:messages.created'));
      }

      resetModal();
      fetchData();
    } catch (error) {
      console.error('Error saving report:', error);
      const data = error.response?.data;
      let msg = '';
      if (data) {
        if (data.detail) {
          msg = data.detail;
        } else if (data.non_field_errors) {
          msg = data.non_field_errors.join('; ');
        } else if (typeof data === 'object') {
          msg = Object.entries(data)
            .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
            .join('\n');
        } else {
          msg = String(data);
        }
      }
      alert('Ошибка: ' + (msg || error.message));
    }
  };

  const handleSubmitReport = async (id) => {
    if (window.confirm(t('reports:actions.confirmSubmit') + ' ' + t('reports:actions.confirmSubmitNote'))) {
      try {
        await api.submitReport(id);
        fetchData();
      } catch (error) {
        console.error('Error submitting report:', error);
        const data = error.response?.data;
        let msg = data?.detail || (data?.non_field_errors && data.non_field_errors.join('; ')) || error.message;
        alert('Ошибка: ' + msg);
      }
    }
  };

  const handleCompleteTask = async () => {
    if (!completeTarget) return;
    try {
      await api.completeExecution(completeTarget.id, { result_text: resultText });
      setShowCompleteModal(false);
      setCompleteTarget(null);
      setResultText('');
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openCompleteModal = (task) => {
    setCompleteTarget(task);
    setResultText('');
    setShowCompleteModal(true);
  };

  const handleCarryOver = async () => {
    if (!weeklyPlan) return;
    if (!window.confirm(t('plans:messages.confirmCarryOver'))) return;
    try {
      setCarryingOver(true);
      const endDate = new Date(weeklyPlan.end_date);
      const nextStart = new Date(endDate);
      nextStart.setDate(nextStart.getDate() + 1);
      const nextEnd = new Date(nextStart);
      nextEnd.setDate(nextEnd.getDate() + 6);
      await api.carryOverTasks(weeklyPlan.id, {
        new_start_date: nextStart.toISOString().split('T')[0],
        new_end_date: nextEnd.toISOString().split('T')[0],
      });
      alert(t('plans:messages.nextWeekCreated'));
      navigate('/plans');
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCarryingOver(false);
    }
  };

  const handleSubmitExplanation = async (expId) => {
    const file = expFile[expId];
    if (!file) {
      alert(isRu ? 'Выберите PDF файл' : 'PDF faylni tanlang');
      return;
    }
    setExpUploading(expId);
    try {
      await submitExplanation(expId, file);
      alert(isRu ? 'Объяснительная отправлена!' : 'Tushuntirish xati yuborildi!');
      setExpFile(prev => { const n = { ...prev }; delete n[expId]; return n; });
      // обновить список
      const expRes = await getExplanations({ employee: user.id });
      const pending = (expRes.data.results || expRes.data || []).filter(
        e => e.status === 'requested' || e.status === 'submitted'
      );
      setMyExplanations(pending);
    } catch (err) {
      alert(isRu ? 'Ошибка загрузки файла' : 'Faylni yuklashda xatolik');
    }
    setExpUploading(null);
  };

  const getTaskStatusLabel = (status) => {
    return t(`plans:taskStatusLabels.${status}`, { defaultValue: status });
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      not_started: '#94a3b8',
      in_progress: '#3b82f6',
      completed: '#10b981',
      cancelled: '#6b7280',
      on_hold: '#f59e0b',
    };
    return colors[status] || '#94a3b8';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  // Attendance helpers
  const ATTENDANCE_STATUSES = [
    { value: 'present', label: 'На работе', color: '#22c55e', bg: '#f0fdf4' },
    { value: 'late_excused', label: 'Опоздал (уважит.)', color: '#f59e0b', bg: '#fffbeb' },
    { value: 'late_unexcused', label: 'Опоздал (неуважит.)', color: '#ef4444', bg: '#fef2f2' },
    { value: 'on_errand', label: 'Отпросился на время', color: '#8b5cf6', bg: '#f5f3ff' },
    { value: 'vacation', label: 'В отпуске', color: '#3b82f6', bg: '#eff6ff' },
    { value: 'sick_leave', label: 'На больничном', color: '#f59e0b', bg: '#fffbeb' },
    { value: 'maternity', label: 'Уход за ребёнком', color: '#8b5cf6', bg: '#f5f3ff' },
    { value: 'pregnancy_leave', label: 'Декретный отпуск', color: '#ec4899', bg: '#fdf2f8' },
    { value: 'business_trip', label: 'В командировке', color: '#06b6d4', bg: '#ecfeff' },
    { value: 'absent', label: 'Отсутствует', color: '#ef4444', bg: '#fef2f2' },
  ];

  const handleDeptAttendanceChange = (empId, newStatus) => {
    setDeptAttendance(prev => ({ ...prev, [empId]: { ...prev[empId], status: newStatus } }));
  };

  const saveDeptAttendance = async () => {
    setAttSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const records = Object.entries(deptAttendance).map(([empId, data]) => ({
        employee_id: parseInt(empId),
        status: data.status,
        note: data.note || '',
      }));
      await bulkMarkAttendance({ date: today, records });
      setShowAttendanceForm(false);
    } catch (err) {
      console.error('Error saving attendance:', err);
    }
    setAttSaving(false);
  };

  const getAttStatusInfo = (val) => ATTENDANCE_STATUSES.find(s => s.value === val) || ATTENDANCE_STATUSES[0];

  const getStatusBadge = (status) => {
    const icons = {
      draft: <Edit size={14} />,
      submitted: <Send size={14} />,
      in_review: <AlertCircle size={14} />,
      approved: <CheckCircle size={14} />,
      rejected: <AlertCircle size={14} />,
    };
    const cssClasses = {
      draft: 'status-draft',
      submitted: 'status-submitted',
      in_review: 'status-review',
      approved: 'status-approved',
      rejected: 'status-rejected',
    };
    return (
      <span className={`status-badge ${cssClasses[status] || 'status-draft'}`}>
        {icons[status] || icons.draft}
        {t(`common:reportStatuses.${status}`, { defaultValue: status })}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-spinner">{t('common:table.loading')}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>{t('common:table.department')}</h1>
          <p className="subtitle">
            {user?.department_name || t('common:table.department')}
          </p>
        </div>
      </div>

      {/* Welcome Card */}
      <div className="welcome-card">
        <Users size={24} className="welcome-icon" />
        <div>
          <h2>Добро пожаловать, {user?.first_name || user?.username}!</h2>
          <p>{t('common:table.department')}: {user?.department_name}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
          <button className="btn-primary" onClick={() => { setEditingReport(null); setShowCreateModal(true); }}>
            <Plus size={18} />
            {t('reports:actions.createReport')}
          </button>
        </div>
      </div>

      {/* Active Plans Notification */}
      {activePlans.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          border: '1px solid #6ee7b7',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          animation: 'fadeInUp 0.5s ease',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '10px',
            background: '#10b981', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Target size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#065f46', fontSize: '0.95rem' }}>
              {t('plans:activePlansBanner', { count: activePlans.length })}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#047857', marginTop: '0.2rem' }}>
              {activePlans.map(p => p.title).join(', ')}
            </div>
          </div>
          <button
            onClick={() => navigate('/plans')}
            style={{
              padding: '0.5rem 1rem', background: '#10b981', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontWeight: 500, fontSize: '0.85rem', display: 'flex',
              alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap',
            }}
          >
            <ClipboardList size={16} />
            {t('plans:actions.goToPlans')}
          </button>
        </div>
      )}

      {/* План на текущую неделю */}
      {weeklyPlan && !weeklyPlan._isPast && (
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '1.25rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, color: '#1e293b' }}>
              <CalendarCheck size={20} style={{ color: '#3b82f6' }} />
              {t('plans:weeklyPlan.title')}
            </h2>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              {formatDate(weeklyPlan.start_date)} — {formatDate(weeklyPlan.end_date)}
            </span>
          </div>

          {/* Прогресс-бар */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: '0.35rem' }}>
              <span>{t('plans:weeklyPlan.completed', { completed: weeklyTasks.filter(tk => tk.status === 'completed').length, total: weeklyTasks.length })}</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>
                {weeklyTasks.length > 0
                  ? Math.round((weeklyTasks.filter(tk => tk.status === 'completed').length / weeklyTasks.length) * 100)
                  : 0}%
              </span>
            </div>
            <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                borderRadius: 4,
                background: weeklyTasks.length > 0
                  ? (weeklyTasks.filter(tk => tk.status === 'completed').length / weeklyTasks.length) >= 0.8
                    ? '#10b981'
                    : (weeklyTasks.filter(tk => tk.status === 'completed').length / weeklyTasks.length) >= 0.5
                      ? '#f59e0b'
                      : '#ef4444'
                  : '#e2e8f0',
                width: weeklyTasks.length > 0
                  ? `${(weeklyTasks.filter(tk => tk.status === 'completed').length / weeklyTasks.length) * 100}%`
                  : '0%',
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>

          {/* Список задач */}
          {weeklyTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontSize: '0.88rem' }}>
              {t('plans:tasks.noTasks')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {weeklyTasks.slice(0, 8).map(task => (
                <div key={task.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.6rem 0.75rem', background: '#f8fafc', borderRadius: '8px',
                  fontSize: '0.88rem',
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: getTaskStatusColor(task.status),
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {task.title}
                    </div>
                  </div>
                  {task.is_carried_over && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      padding: '0.15rem 0.5rem', background: '#fef3c7', color: '#92400e',
                      borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500, flexShrink: 0,
                    }}>
                      <RefreshCw size={11} /> {t('plans:carryOver.carried')}{task.carry_count > 1 ? ` (${task.carry_count})` : ''}
                    </span>
                  )}
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                    fontWeight: 500, flexShrink: 0,
                    background: task.status === 'completed' ? '#d1fae5' : task.status === 'in_progress' ? '#dbeafe' : '#f1f5f9',
                    color: task.status === 'completed' ? '#065f46' : task.status === 'in_progress' ? '#1e40af' : '#475569',
                  }}>
                    {getTaskStatusLabel(task.status)}
                  </span>
                  {task.completion_percentage != null && task.status !== 'completed' && task.status !== 'not_started' && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }}>
                      {task.completion_percentage}%
                    </span>
                  )}
                </div>
              ))}
              {weeklyTasks.length > 8 && (
                <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#64748b', paddingTop: '0.25rem' }}>
                  {t('plans:weeklyPlan.andMore', { count: weeklyTasks.length - 8 })}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
            <button
              onClick={() => navigate(`/plans/${weeklyPlan.id}`)}
              style={{
                padding: '0.45rem 0.85rem', background: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.82rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.35rem',
              }}
            >
              <Eye size={14} /> {t('common:buttons.details')}
            </button>
          </div>
        </div>
      )}

      {/* Невыполненные задачи прошлой недели / Перенос */}
      {weeklyPlan && weeklyPlan._isPast && carriedTasks.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          border: '1px solid #fcd34d',
          borderRadius: '12px',
          padding: '1.25rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, color: '#92400e' }}>
              <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
              {t('plans:weeklyPlan.unfinishedLastWeek')} ({carriedTasks.length})
            </h2>
            <span style={{ fontSize: '0.82rem', color: '#92400e' }}>
              {formatDate(weeklyPlan.start_date)} — {formatDate(weeklyPlan.end_date)}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {carriedTasks.map(task => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '8px',
                fontSize: '0.88rem',
              }}>
                <RefreshCw size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: '#1e293b' }}>{task.title}</div>
                  {task.responsible_name && (
                    <div style={{ fontSize: '0.78rem', color: '#78716c', marginTop: '0.15rem' }}>
                      <User size={11} /> {task.responsible_name}
                    </div>
                  )}
                </div>
                {task.carry_count > 0 && (
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                    background: task.carry_count > 2 ? '#fee2e2' : '#fef3c7',
                    color: task.carry_count > 2 ? '#991b1b' : '#92400e',
                    flexShrink: 0,
                  }}>
                    {t('plans:carryOver.carriedCount')}: {task.carry_count}
                  </span>
                )}
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                  fontWeight: 500, flexShrink: 0,
                  background: '#f1f5f9', color: '#475569',
                }}>
                  {getTaskStatusLabel(task.status)}
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCarryOver}
              disabled={carryingOver}
              style={{
                padding: '0.5rem 1rem', background: '#f59e0b', color: 'white',
                border: 'none', borderRadius: '8px', cursor: carryingOver ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.4rem',
                opacity: carryingOver ? 0.7 : 1,
              }}
            >
              <ArrowRight size={16} />
              {carryingOver ? t('plans:actions.creating') : t('plans:actions.createNextWeek')}
            </button>
          </div>
        </div>
      )}

      {/* Перенесённые задачи в текущем плане */}
      {weeklyPlan && !weeklyPlan._isPast && carriedTasks.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          border: '1px solid #fcd34d',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex', alignItems: 'center', gap: '1rem',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '8px',
            background: '#f59e0b', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <RefreshCw size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.92rem' }}>
              {carriedTasks.length} {t('plans:weeklyPlan.carriedFromLastWeek')}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#a16207', marginTop: '0.15rem' }}>
              {carriedTasks.filter(tk => tk.carry_count > 2).length > 0
                ? t('plans:weeklyPlan.moreThan2Carries', { count: carriedTasks.filter(tk => tk.carry_count > 2).length })
                : t('plans:weeklyPlan.payAttention')}
            </div>
          </div>
        </div>
      )}

      {/* Сотрудники отдела */}
      {deptEmployees.length > 0 && (
        <div className="section" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              <Users size={20} />
              Сотрудники отдела ({deptEmployees.length})
            </h2>
            {/* Кнопка доступна только если HR не подтвердил посещаемость */}
            {!attConfirmedByHR && (user?.role === 'department_head' || user?.role === 'admin' || user?.role === 'director' || user?.role === 'deputy_director') && (
              <button
                className="btn-primary"
                style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
                onClick={() => setShowAttendanceForm(!showAttendanceForm)}
              >
                <CalendarCheck size={16} />
                {showAttendanceForm ? 'Скрыть форму' : 'Отметить посещаемость'}
              </button>
            )}
            {attConfirmedByHR && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#16a34a', backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
                <ShieldCheck size={15} />
                Подтверждено отделом кадров
              </span>
            )}
          </div>

          {/* Баннер блокировки редактирования */}
          {attConfirmedByHR && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', marginBottom: '12px', borderRadius: '8px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', fontSize: '14px', color: '#166534' }}>
              <Lock size={16} />
              <div>
                <strong>Посещаемость закрыта для редактирования.</strong>{' '}
                Отдел кадров подтвердил посещаемость за сегодня. Обратитесь в HR для исправлений.
              </div>
            </div>
          )}

          {/* Сводка посещаемости */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            {(() => {
              const counts = {};
              ATTENDANCE_STATUSES.forEach(st => { counts[st.value] = 0; });
              Object.values(deptAttendance).forEach(a => { counts[a.status] = (counts[a.status] || 0) + 1; });
              return ATTENDANCE_STATUSES.filter(st => counts[st.value] > 0).map(st => (
                <span key={st.value} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600, color: st.color, backgroundColor: st.bg }}>
                  {st.label}: {counts[st.value]}
                </span>
              ));
            })()}
          </div>

          {/* Таблица сотрудников */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>ФИО</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Должность</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', width: showAttendanceForm ? '180px' : '120px' }}>Статус сегодня</th>
                </tr>
              </thead>
              <tbody>
                {deptEmployees.map((emp, idx) => {
                  const att = deptAttendance[emp.id] || { status: 'present' };
                  const info = getAttStatusInfo(att.status);
                  return (
                    <tr key={emp.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 500 }}>{emp.full_name}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{emp.position || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                        {showAttendanceForm && !attConfirmedByHR ? (
                          <select
                            value={att.status}
                            onChange={e => handleDeptAttendanceChange(emp.id, e.target.value)}
                            style={{ padding: '4px 8px', border: `1px solid ${info.color}`, borderRadius: '6px', fontSize: '0.82rem', color: info.color, outline: 'none', width: '100%' }}
                          >
                            {ATTENDANCE_STATUSES.map(st => (
                              <option key={st.value} value={st.value}>{st.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600, color: info.color, backgroundColor: info.bg }}>
                            {info.label}
                            {attConfirmedByHR && <Lock size={11} />}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showAttendanceForm && !attConfirmedByHR && (
            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn-primary"
                style={{ fontSize: '0.85rem' }}
                onClick={saveDeptAttendance}
                disabled={attSaving}
              >
                {attSaving ? <RefreshCw size={16} /> : <CheckCircle size={16} />}
                Сохранить посещаемость
              </button>
            </div>
          )}
        </div>
      )}

      {/* HR Tools — только для отдела кадров */}
      {user?.department_code === 'HR' && (
        <div className="section" style={{ marginBottom: '1.5rem' }}>
          <HRDashboard embedded />
        </div>
      )}

      {/* Объяснительные по опозданиям */}
      {myExplanations.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          border: '1px solid #fcd34d',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: 600, color: '#92400e', margin: '0 0 0.75rem' }}>
            <AlertCircle size={18} color="#d97706" />
            {isRu
              ? `Запрошены объяснительные (${myExplanations.length})`
              : `Tushuntirish xatlari so'raldi (${myExplanations.length})`}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {myExplanations.map(exp => (
              <div key={exp.id} style={{
                background: '#fff',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                    {isRu ? 'Дата опоздания:' : 'Kechikish sanasi:'} {exp.date}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    {isRu ? 'Запросил:' : "So'ragan:"} {exp.requested_by_name || '—'}
                    {exp.status === 'submitted' && (
                      <span style={{ marginLeft: '8px', color: '#2563eb', fontWeight: 500 }}>
                        ✓ {isRu ? 'Объяснительная представлена' : 'Tushuntirish topshirildi'}
                      </span>
                    )}
                  </div>
                </div>
                {exp.status === 'requested' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="file"
                      accept=".pdf"
                      id={`exp-file-${exp.id}`}
                      style={{ display: 'none' }}
                      onChange={e => {
                        const f = e.target.files[0];
                        if (f) setExpFile(prev => ({ ...prev, [exp.id]: f }));
                      }}
                    />
                    <label
                      htmlFor={`exp-file-${exp.id}`}
                      style={{
                        padding: '0.4rem 0.8rem', background: '#f1f5f9', color: '#475569',
                        border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.82rem', whiteSpace: 'nowrap',
                      }}
                    >
                      {expFile[exp.id] ? expFile[exp.id].name : (isRu ? 'Выбрать PDF' : 'PDF tanlash')}
                    </label>
                    <button
                      style={{
                        padding: '0.4rem 0.8rem', background: expUploading === exp.id ? '#94a3b8' : '#d97706',
                        color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.82rem', fontWeight: 500,
                      }}
                      disabled={expUploading === exp.id}
                      onClick={() => handleSubmitExplanation(exp.id)}
                    >
                      {expUploading === exp.id
                        ? (isRu ? 'Отправка...' : 'Yuborilmoqda...')
                        : (isRu ? 'Отправить' : 'Yuborish')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Поручения отдела */}
      {pendingTasks.length > 0 && (
        <div className="section">
          <h2 className="section-title" style={{ color: '#dc2626' }}>
            <AlertTriangle size={20} />
            {t('documents:misc.pendingTasks')} ({pendingTasks.length})
          </h2>
          <div className="pending-tasks-list">
            {pendingTasks.map(task => (
              <div key={task.id} className={`pending-task-card ${task.is_overdue ? 'overdue' : ''}`}>
                <div className="pending-task-main">
                  <div className="pending-task-info">
                    <div className="pending-task-doc">
                      <FileText size={14} />
                      <span
                        className="pending-task-doc-link"
                        onClick={() => navigate(`/documents/${task.document_id}`)}
                      >
                        {task.document_reg_number} — {task.document_title}
                      </span>
                    </div>
                    <div className="pending-task-text">
                      п. {task.item_number}: {task.item_text}
                    </div>
                    <div className="pending-task-meta">
                      {task.executor_name && (
                        <span><User size={12} /> {task.executor_name}</span>
                      )}
                      {task.deadline && (
                        <span className={task.is_overdue ? 'text-danger' : ''}>
                          <Calendar size={12} /> {t('common:table.deadline')}: {formatDate(task.deadline)}
                        </span>
                      )}
                      {task.is_overdue && (
                        <span className="overdue-badge">{t('documents:misc.overdue')}</span>
                      )}
                      {task.is_responsible && (
                        <span className="responsible-badge-small">{t('documents:executors.responsible')}</span>
                      )}
                    </div>
                  </div>
                  <div className="pending-task-action">
                    <button
                      className="btn-complete-task"
                      onClick={() => openCompleteModal(task)}
                    >
                      <CheckCircle size={16} />
                      {t('common:buttons.complete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reports Grid */}
      <div className="section">
        <h2 className="section-title">
          <FileText size={20} />
          {t('reports:pageTitles.myReports')} ({reports.length})
        </h2>

        {reports.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>{t('reports:noReports')}</h3>
            <p>{t('reports:createFirst')}</p>
          </div>
        ) : (
          <div className="reports-grid">
            {reports.map(report => (
              <div key={report.id} className="report-card">
                <div className="report-card-header">
                  <h3>{report.title}</h3>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {getStatusBadge(report.status)}
                    {report.is_on_time === true && (
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#16a34a' }}>
                        {t('reports:timing.onTime')}
                      </span>
                    )}
                    {report.is_on_time === false && (
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef2f2', color: '#dc2626' }}>
                        +{report.delay_hours || 0}ч
                      </span>
                    )}
                  </div>
                </div>
                <div className="report-card-body">
                  <div className="report-meta">
                    <Calendar size={16} />
                    <span>{report.period_name || t('common:table.period')}</span>
                  </div>
                  <p className="report-summary">{report.summary || t('reports:noDescription')}</p>
                  {report.rejection_reason && (
                    <div className="rejection-notice">
                      <AlertCircle size={16} />
                      <span>{t('reports:actions.rejectionReason')}: {report.rejection_reason}</span>
                    </div>
                  )}
                </div>
                <div className="report-card-footer">
                  <button className="btn-secondary" onClick={() => navigate(`/reports/${report.id}`)}>
                    <Eye size={16} />
                    {t('common:actions.view')}
                  </button>
                  {(report.status === 'draft' || report.status === 'rejected') && (
                    <button className="btn-secondary" onClick={() => handleEditReport(report)}>
                      <Edit size={16} />
                      {t('common:buttons.edit')}
                    </button>
                  )}
                  {report.status === 'draft' && (
                    <button className="btn-primary" onClick={() => handleSubmitReport(report.id)}>
                      <Send size={16} />
                      {t('common:buttons.submit')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модал выполнения поручения */}
      {showCompleteModal && completeTarget && (
        <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('documents:executors.markComplete')}</h2>
              <button className="modal-close" onClick={() => setShowCompleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', fontSize: '0.88rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  {completeTarget.document_reg_number} — {completeTarget.document_title}
                </div>
                <div style={{ color: '#64748b' }}>
                  п. {completeTarget.item_number}: {completeTarget.item_text}
                </div>
              </div>
              <div className="form-group">
                <label>{t('documents:executors.completionReport')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('documents:executors.completionPlaceholder')}
                  value={resultText}
                  onChange={e => setResultText(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCompleteModal(false)}>{t('common:buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleCompleteTask}>
                <CheckCircle size={16} /> {t('documents:executors.markComplete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingReport ? t('reports:pageTitles.editReport') : t('reports:pageTitles.createReport')}</h2>
              <button className="modal-close" onClick={resetModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>{t('reports:form.reportTitle')} *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('reports:form.reportTitlePlaceholder')}
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('reports:form.period')} *</label>
                <select
                  className="form-control"
                  value={formData.period}
                  onChange={e => handlePeriodChange(e.target.value)}
                  required
                >
                  <option value="">{t('reports:form.selectPeriod')}</option>
                  {periods.map(period => (
                    <option key={period.id} value={period.id}>
                      {period.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Динамические поля из шаблона отдела */}
              {activeTemplate && activeTemplate.fields_structure && activeTemplate.fields_structure.length > 0 && (
                <div className="template-fields-section">
                  <div className="template-section-header">
                    <h4><BarChart3 size={16} /> {t('reports:sections.departmentTemplate')}</h4>
                    <span className="completion-badge">
                      {t('reports:template.filledOf', { filled: activeTemplate.fields_structure.filter(f => f.required && departmentFields[f.key] && String(departmentFields[f.key]).trim() !== '').length, total: activeTemplate.fields_structure.filter(f => f.required).length })}
                    </span>
                  </div>

                  {activeTemplate.fields_structure.map(field => (
                    <div className="form-group" key={field.key}>
                      <label>
                        {field.label} {field.required && <span style={{ color: '#e74c3c' }}>*</span>}
                      </label>
                      {field.hint && (
                        <small className="field-hint">{field.hint}</small>
                      )}
                      {field.type === 'textarea' ? (
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder={field.label}
                          value={departmentFields[field.key] || ''}
                          onChange={e => setDepartmentFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                        />
                      ) : field.type === 'number' ? (
                        <div>
                          <input
                            type="number"
                            className="form-control"
                            placeholder={field.label}
                            value={departmentFields[field.key] || ''}
                            onChange={e => setDepartmentFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                          />
                          {lastReportData && lastReportData.department_data && lastReportData.department_data[field.key] !== undefined && (
                            <small className="last-report-hint">
                              {t('reports:template.lastReport')}: {lastReportData.department_data[field.key]}
                            </small>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          placeholder={field.label}
                          value={departmentFields[field.key] || ''}
                          onChange={e => setDepartmentFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="form-group">
                <label>{t('reports:form.summary')}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('reports:form.summaryPlaceholder')}
                  value={formData.summary}
                  onChange={e => setFormData({...formData, summary: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('reports:form.mainWork')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('reports:form.mainWorkPlaceholder')}
                  value={formData.main_work}
                  onChange={e => setFormData({...formData, main_work: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('reports:form.achievements')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('reports:form.achievementsPlaceholder')}
                  value={formData.achievements}
                  onChange={e => setFormData({...formData, achievements: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('reports:form.problems')}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('reports:form.problemsPlaceholder')}
                  value={formData.problems}
                  onChange={e => setFormData({...formData, problems: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('reports:form.plansNextPeriod')}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('reports:form.plansNextPeriodPlaceholder')}
                  value={formData.plans_next_period}
                  onChange={e => setFormData({...formData, plans_next_period: e.target.value})}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={resetModal}>
                {t('common:buttons.cancel')}
              </button>
              <button className="btn-primary" onClick={handleSaveReport}>
                {editingReport ? (
                  <><Edit size={18} /> {t('common:buttons.save')}</>
                ) : (
                  <><Plus size={18} /> {t('reports:actions.createReport')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentDashboard;